import {
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { Box } from "@mui/system";
import dayjs from "dayjs";
import type { JSX } from "react";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { SprengnetterValuationHubValuationPackageModelResponseValuationPackageListItemResponse as ValuationListItem } from "@/api/generatedApi";
import { Building01Icon, Building07Icon, Home03Icon, PlotIcon2 } from "@/assets";
import { getObjectTypeLabelWIthBuildingType } from "@/helpers/realEstateParametersHelpers";
import { ObjectTypeEnum } from "@/types/refineRealEstate";

import { SortableTableCell } from "./SortableTableCell";
import { ValuationActions } from "./ValuationActions";
import { SortDirection, SortableColumn } from "./ValuationListPage";

const styles = {
  table: {
    tableLayout: "auto",
    width: "100%",
    "& .MuiTableRow-root": {
      backgroundColor: "white !important",
    },
    "& thead": {
      position: "sticky",
      top: 0,
      backgroundColor: "white",
      zIndex: 10,
    },
    "& thead tr": {
      opacity: 0.6,
      borderBottom: "1px solid #E9EAEB",
    },
    "& tbody tr": {
      borderBottom: "1px solid #E9EAEB",
    },
    "& tbody tr:last-child": {
      borderBottom: "none",
    },
    "& th": {
      height: "44px",
      fontWeight: 600,
      fontSize: "14px",
      color: "#333333",
      px: 2,
    },
    "& td": {
      height: "44px",
      fontWeight: 600,
      fontSize: "13px",
      color: "#333333",
      px: 2,
    },
    "& thead tr th:first-of-type": {
      pl: 0,
    },
    "& tbody tr th:first-of-type": {
      pl: 0,
    },
  },
  secondaryText: {
    fontSize: "13px",
    fontWeight: 600,
    opacity: "0.6",
  },
  tableContainer: {
    position: "relative",
    height: "100%",
    width: "100%",
    overflow: "auto",
    display: "block",
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    bgcolor: "rgba(255, 255, 255, 0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 11,
  },
  addressLine1: {
    fontSize: "13px",
    fontWeight: 600,
  },
  actionsTableCell: {
    opacity: 1,
  },
  clickableRow: {
    cursor: "pointer",
  },
  truncatedCellContent: {
    display: "block",
    maxWidth: "220px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  lightTooltip: {
    bgcolor: "#FFFFFF",
    color: "#333333",
    border: "1px solid #E9EAEB",
    boxShadow: "none",
    fontSize: "12px",
    fontWeight: 400,
  },
  emptyTableBody: {
    textAlign: "center",
    py: 8,
  },
};

interface ValuationsListProps {
  valuations: ValuationListItem[];
  onDeleted?: (id: string) => void;
  onSortChange?: (sortBy: SortableColumn, direction: SortDirection) => void;
  sortBy: SortableColumn;
  sortDirection: SortDirection;
  isLoading?: boolean;
  isFetching?: boolean;
  hideDelete?: boolean;
}

const objectTypeIcons: Record<ObjectTypeEnum, JSX.Element> = {
  [ObjectTypeEnum.house]: <Home03Icon color="primary" />,
  [ObjectTypeEnum.apartment]: <Building01Icon color="primary" />,
  [ObjectTypeEnum.multiFamilyHouse]: <Building07Icon color="primary" />,
  [ObjectTypeEnum.mixedUseProperty]: <Building07Icon color="primary" />,
  [ObjectTypeEnum.plotOfLand]: <PlotIcon2 color="primary" />,
};

const isExpired = (validUntil?: string | null): boolean => {
  if (!validUntil) return false;
  return dayjs().isAfter(dayjs(validUntil));
};

const formatAddress = (address?: ValuationListItem["address"]): { line1: string; line2: string } => {
  if (!address) return { line1: "", line2: "" };

  const line1 = [address.street, address.house_number].filter(Boolean).join(" ");
  const line2 = [address.post_code, address.town].filter(Boolean).join(" ");

  return {
    line1: line1 || "",
    line2: line2 || "",
  };
};

interface TruncatedTextWithTooltipProps {
  text?: string | null;
}

const TruncatedTextWithTooltip = ({ text }: TruncatedTextWithTooltipProps): JSX.Element => {
  const textRef = useRef<HTMLSpanElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const element = textRef.current;

    if (!element) {
      setIsTruncated(false);
      return;
    }

    const updateTruncation = (): void => {
      setIsTruncated(element.scrollWidth > element.clientWidth);
    };

    updateTruncation();

    const resizeObserver = new ResizeObserver(updateTruncation);
    resizeObserver.observe(element);
    window.addEventListener("resize", updateTruncation);

    return (): void => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateTruncation);
    };
  }, [text]);

  return (
    <Tooltip
      title={text ?? ""}
      placement="top-start"
      disableHoverListener={!isTruncated}
      slotProps={{
        tooltip: {
          sx: styles.lightTooltip,
        },
      }}
    >
      <Box component="span" ref={textRef} sx={styles.truncatedCellContent}>
        {text}
      </Box>
    </Tooltip>
  );
};

export const ValuationsList = ({
  valuations,
  onDeleted,
  onSortChange,
  sortBy,
  sortDirection,
  isLoading = false,
  isFetching = false,
  hideDelete = false,
}: ValuationsListProps): JSX.Element => {
  const { t } = useTranslation("ValuationListPage");
  const { t: tRealEstate } = useTranslation("Common", { keyPrefix: "realEstate" });
  const navigate = useNavigate();

  const handleSort = useCallback(
    (column: SortableColumn): void => {
      if (!onSortChange) return;

      const newDirection = sortBy === column && sortDirection === "Ascending" ? "Descending" : "Ascending";

      onSortChange(column, newDirection);
    },
    [sortBy, sortDirection, onSortChange],
  );

  const onTableRowClick = (valuation: ValuationListItem): void => {
    navigate(`/valuations/${valuation.id}`);
  };

  return (
    <TableContainer component={Paper} elevation={0} sx={styles.tableContainer}>
      {isFetching && (
        <Box sx={styles.loadingOverlay}>
          <CircularProgress size={40} />
        </Box>
      )}

      <Table sx={styles.table}>
        <TableHead>
          <TableRow>
            <SortableTableCell
              column="Street"
              label={t("valuationsListTable.object")}
              currentSortBy={sortBy}
              sortDirection={sortDirection}
              onSort={handleSort}
              data-cy="address-header"
            />
            <TableCell>{t("valuationsListTable.category")}</TableCell>
            <TableCell>{t("valuationsListTable.user")}</TableCell>
            <SortableTableCell
              column="CreatedAt"
              label={t("valuationsListTable.creationDate")}
              currentSortBy={sortBy}
              sortDirection={sortDirection}
              onSort={handleSort}
              data-cy="creation-date-header"
            />
            <SortableTableCell
              column="AskingPrice"
              label={t("valuationsListTable.askingPrice")}
              currentSortBy={sortBy}
              sortDirection={sortDirection}
              onSort={handleSort}
              data-cy="asking-price-header"
            />
            <TableCell>{t("valuationsListTable.actions")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!isFetching && isLoading && valuations.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} sx={styles.emptyTableBody}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : (
            valuations.map((valuation) => {
              const address = formatAddress(valuation.address);
              return (
                <TableRow key={valuation.id} hover sx={styles.clickableRow} onClick={() => onTableRowClick(valuation)}>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      ...(isExpired(valuation.valid_until) ? styles.secondaryText : {}),
                    }}
                  >
                    <Stack>
                      <Box sx={styles.addressLine1}>{address.line1}</Box>
                      <Box sx={styles.secondaryText}>{address.line2}</Box>
                    </Stack>
                  </TableCell>
                  <TableCell
                    sx={{
                      ...(isExpired(valuation.valid_until) ? styles.secondaryText : {}),
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      {objectTypeIcons[valuation.object_type as ObjectTypeEnum]}
                      <span>
                        {getObjectTypeLabelWIthBuildingType(
                          valuation.object_type,
                          tRealEstate,
                          valuation.building_type,
                        )}
                      </span>
                    </Stack>
                  </TableCell>
                  <TableCell
                    sx={{
                      ...(isExpired(valuation.valid_until) ? styles.secondaryText : {}),
                    }}
                  >
                    <TruncatedTextWithTooltip text={valuation.created_by} />
                  </TableCell>
                  <TableCell
                    sx={{
                      ...(isExpired(valuation.valid_until) ? styles.secondaryText : {}),
                    }}
                  >
                    <Stack>
                      {isExpired(valuation.valid_until) && <span>{t("expired")}</span>}
                      <span>{dayjs(valuation.created_at).format("DD.MM.YYYY")}</span>
                    </Stack>
                  </TableCell>
                  <TableCell
                    sx={{
                      ...(isExpired(valuation.valid_until) ? styles.secondaryText : {}),
                    }}
                  >
                    {valuation.asking_price ? (
                      <Stack>
                        <Box>{valuation.asking_price.toLocaleString("de-DE")} €</Box>
                        {valuation.living_area && (
                          <Box sx={styles.secondaryText}>
                            {(valuation.asking_price / valuation.living_area).toLocaleString("de-DE", {
                              maximumFractionDigits: 2,
                            })}{" "}
                            €/m²
                          </Box>
                        )}
                      </Stack>
                    ) : (
                      <span style={styles.secondaryText}>{t("notEntered")}</span>
                    )}
                  </TableCell>
                  <TableCell
                    sx={styles.actionsTableCell}
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                  >
                    <ValuationActions
                      id={valuation.id}
                      referenceNumber={valuation.reference_number}
                      onDeleted={onDeleted}
                      isExpired={isExpired(valuation.valid_until)}
                      hideDelete={hideDelete}
                    />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
